import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { DocusignFieldsForm } from 'src/components/docusign-field-form/DocusignFieldsForm'
import {
  FetchAcceptCustomerInviteDocusignSigningEnvelopeOptions,
  AcceptCustomerInviteDocusignStartSigning,
  AcceptCustomerInviteDocusignCompleteSigning,
  AcceptCustomerInviteDocusignDeclineSigning,
  FetchUser,
} from 'src/constants/actionTypes'
import {
  DocusignSigningEnvelopeOptionsResponse,
  DocusignSigningField,
  DocusignStartSigningResponse,
} from 'src/interfaces/docusign'
import { useQuery } from 'src/utils/hooks/useQuery'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { captureSentryError } from 'src/utils/sentry'
import { showToast } from 'src/utils/toast'
import tw from 'twin.macro'
import { SignContractAgreementModal } from 'src/components/docusign-field-form/SignContractAgreementModal'
import { CustomerSignContractGetStarted } from './CustomerSignContractGetStarted'
import { CustomerFormHeader } from '../shared/CustomerFormHeader'

const DOCUSIGN_CONTAINER_ID = 'docusign-container'

interface SigningField {
  tabLabel: string
  value: string
}

interface Props {
  customerId: string
  stepNumber?: number
  stepCount?: number
  onSubmit?: () => void
}

const AcceptCustomerInviteSignContractComponent = ({
  customerId,
  stepNumber,
  stepCount,
  onSubmit,
}: Props) => {
  const dispatch = useDispatch()
  const { getQuery } = useQuery()

  const [docusignSigningData, setDocusignSigningData] =
    useState<DocusignStartSigningResponse | null>(null)
  const [signingEnvelopeOptions, setSigningEnvelopeOptions] =
    useState<DocusignSigningEnvelopeOptionsResponse | null>(null)
  const [isStartLoading, setIsStartLoading] = useState<boolean>(false)
  const [isSigningFallbackLoading, setIsSigningFallbackLoading] = useState<boolean>(false)
  const [isFieldsShown, setIsFieldsShown] = useState<boolean>(false)
  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState<boolean>(false)
  const [fields, setFields] = useState<Array<DocusignSigningField>>([])
  const [signingIframeCrashed, setSigningIframeCrashed] = useState<boolean>(false)

  const signingIframeLoadCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const signingIframeCorsCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const signingIframeExistsCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const clearSigningTimers = useCallback(() => {
    if (signingIframeLoadCheckTimeoutRef.current) {
      clearTimeout(signingIframeLoadCheckTimeoutRef.current)
      signingIframeLoadCheckTimeoutRef.current = null
    }

    if (signingIframeCorsCheckIntervalRef.current) {
      clearInterval(signingIframeCorsCheckIntervalRef.current)
      signingIframeCorsCheckIntervalRef.current = null
    }

    if (signingIframeExistsCheckIntervalRef.current) {
      clearInterval(signingIframeExistsCheckIntervalRef.current)
      signingIframeExistsCheckIntervalRef.current = null
    }
  }, [])

  useEffect(() => {
    return clearSigningTimers
  }, [clearSigningTimers])

  const docusignReturnEvent = getQuery('event')

  useEffect(() => {
    if (!docusignReturnEvent) {
      return
    }

    if (docusignReturnEvent === 'signing_complete') {
      handleCompleteSigning()
    }
  }, [docusignReturnEvent])

  const agreementContent = useMemo(() => undefined, [])

  const handleLoadDocusignSigningEnvelopeOptions = useCallback(async () => {
    try {
      const response: DocusignSigningEnvelopeOptionsResponse | undefined = await createAsyncAction(
        dispatch,
        FetchAcceptCustomerInviteDocusignSigningEnvelopeOptions.request({ customerId }),
      )

      if (!response) {
        throw new Error('Signing envelope options are not available')
      }

      setSigningEnvelopeOptions(response)
    } catch (err) {
      // ignore this error
    }
  }, [customerId])

  useEffect(() => {
    handleLoadDocusignSigningEnvelopeOptions()
  }, [])

  const handleStartSigning = useCallback(
    async (signingFields?: Array<SigningField>) => {
      setIsStartLoading(true)
      setSigningIframeCrashed(false)

      try {
        const signingResponse: DocusignStartSigningResponse | undefined = await createAsyncAction(
          dispatch,
          AcceptCustomerInviteDocusignStartSigning.request({
            signingFields,
            embedded: true,
            customerId,
          }),
        )

        if (!signingResponse) {
          throw new Error('Signing is not available')
        }

        if (signingResponse.retryFetchDriver) {
          await createAsyncAction(dispatch, FetchUser.request())

          return
        }

        if (signingResponse.signingFields?.length) {
          setFields(signingResponse.signingFields)
          setIsFieldsShown(true)
          setIsStartLoading(false)

          return
        }

        setDocusignSigningData(signingResponse)
        handleLoadDocusign(signingResponse)
      } catch (err) {
        showToast('Failed to load signing page, please try again later', {
          variant: 'error',
        })
      } finally {
        setIsStartLoading(false)
      }
    },
    [customerId],
  )

  const handleStartFallbackSigning = useCallback(async () => {
    setIsSigningFallbackLoading(true)

    try {
      const signingResponse: DocusignStartSigningResponse | undefined = await createAsyncAction(
        dispatch,
        AcceptCustomerInviteDocusignStartSigning.request({
          embedded: false,
          customerId,
        }),
      )

      if (!signingResponse) {
        throw new Error('Signing is not available')
      }

      if (signingResponse?.retryFetchDriver) {
        await createAsyncAction(dispatch, FetchUser.request())

        return
      }

      if (signingResponse?.signingData?.url) {
        window.open(signingResponse?.signingData?.url, '_blank')
      } else {
        throw new Error('Signing is not available')
      }
    } catch (err) {
      showToast('Failed to load signing page, please try again later', {
        variant: 'error',
      })
    } finally {
      setIsSigningFallbackLoading(false)
    }
  }, [customerId])

  const handleCompleteSigning = useCallback(async () => {
    try {
      await createAsyncAction(
        dispatch,
        AcceptCustomerInviteDocusignCompleteSigning.request({ customerId }),
      )
      await createAsyncAction(dispatch, FetchUser.request())

      if (onSubmit) {
        onSubmit()
      }
    } catch (err) {
      showToast('Failed to finish signing, please try again later', {
        variant: 'error',
      })
    }
  }, [customerId, onSubmit])

  const handleDeclineSigning = useCallback(async () => {
    try {
      await createAsyncAction(
        dispatch,
        AcceptCustomerInviteDocusignDeclineSigning.request({ customerId }),
      )
      await createAsyncAction(dispatch, FetchUser.request())
    } catch (err) {
      // ignore this error
    }
  }, [customerId])

  const handleLoadDocusign = useCallback(
    async (payload: DocusignStartSigningResponse) => {
      if (!payload.signingData?.url) {
        return
      }

      try {
        const docusign = await window.DocuSign.loadDocuSign(
          process.env.REACT_APP_DOCUSIGN_INTEGRATION_KEY as string,
        )

        const signing = docusign.signing({
          url: payload.signingData.url,
          displayFormat: 'focused',
          style: {
            branding: {
              primaryButton: {
                backgroundColor: '#333',
                color: '#fff',
              },
            },
            signingNavigationButton: {
              finishText: 'Continue',
              position: 'bottom-center',
            },
          },
        })

        signing.on('ready', () => {
          setIsStartLoading(false)
        })

        signing.on('sessionEnd', async (event) => {
          clearSigningTimers()

          if (event.sessionEndType === 'signing_complete') {
            handleCompleteSigning()

            return
          }

          if (event.sessionEndType === 'ttl_expired') {
            setDocusignSigningData(null)
            showToast('Signing link is expired, please start signing again', {
              variant: 'error',
            })

            return
          }

          if (event.sessionEndType === 'decline') {
            await handleDeclineSigning()
            setDocusignSigningData(null)
            showToast('Signing is declined, please start signing again or contact support', {
              variant: 'error',
            })

            return
          }
        })

        signing.mount(`#${DOCUSIGN_CONTAINER_ID}`)

        startSigningIframeListeners()
      } catch (err: any) {
        setIsStartLoading(false)
        setDocusignSigningData(null)
        setSigningIframeCrashed(true)
        captureSentryError(err, {
          message: '[handleLoadDocusign]',
        })
      }
    },
    [clearSigningTimers],
  )

  const startSigningIframeListeners = useCallback(() => {
    const iframe = document.getElementById('js-library-iframe')

    if (!iframe) {
      setSigningIframeCrashed(true)

      captureSentryError(new Error('Docusign iframe is not found'), {
        message: '[startSigningIframeListeners]',
      })

      return
    }

    let isIframeLoaded = false

    iframe.addEventListener('load', () => {
      isIframeLoaded = true
    })

    signingIframeLoadCheckTimeoutRef.current = setTimeout(() => {
      if (!isIframeLoaded) {
        setSigningIframeCrashed(true)

        captureSentryError(new Error('Docusign iframe is not loaded'), {
          message: '[startSigningIframeListeners]',
        })
      }
    }, 10000)

    iframe.addEventListener('error', () => {
      setSigningIframeCrashed(true)

      captureSentryError(new Error('Docusign iframe is crashed'), {
        message: '[startSigningIframeListeners]',
      })
    })

    signingIframeExistsCheckIntervalRef.current = setInterval(() => {
      if (!document.getElementById('js-library-iframe')) {
        setSigningIframeCrashed(true)

        captureSentryError(new Error('Docusign iframe is not found'), {
          message: '[startSigningIframeListeners]',
        })
      }
    }, 10000)
  }, [])

  useEffect(() => {
    if (!signingIframeCrashed) {
      return
    }

    setDocusignSigningData(null)
    setIsStartLoading(false)
    clearSigningTimers()

    showToast('Failed to start signing, please try again', {
      variant: 'error',
    })
  }, [signingIframeCrashed, clearSigningTimers])

  const handleSubmitFields = useCallback(
    async (signingFields?: Array<SigningField>) => {
      await handleStartSigning(signingFields)

      setIsFieldsShown(false)
    },
    [handleStartSigning],
  )

  const handleOpenAgreementModal = useCallback(() => {
    setIsAgreementModalOpen(true)
  }, [])

  const handleCloseAgreementModal = useCallback(() => {
    setIsAgreementModalOpen(false)
  }, [])

  const handleStartSigningPress = useCallback(() => {
    if (agreementContent) {
      handleOpenAgreementModal()

      return
    }

    handleStartSigning()
  }, [agreementContent, handleStartSigning])

  const handleSubmitAgreement = useCallback(() => {
    handleCloseAgreementModal()
    handleStartSigning()
  }, [handleStartSigning])

  return (
    <div
      css={[
        tw`w-[318px] bg-white rounded-lg`,
        !isFieldsShown ? tw`flex-1 max-h-[612px] flex flex-col justify-between` : undefined,
      ]}
    >
      <CustomerFormHeader title="Accept invite" stepCount={stepCount} stepNumber={stepNumber} />
      {isFieldsShown ? (
        <DocusignFieldsForm fields={fields} onSubmit={handleSubmitFields} />
      ) : (
        <CustomerSignContractGetStarted
          isStartSigningLoading={isStartLoading}
          isSigningFallbackLoading={isSigningFallbackLoading}
          isSigningIframeCrashed={signingIframeCrashed}
          isSigningEnvelopeAvailable={signingEnvelopeOptions?.signingEnvelopeAvailable}
          onStartSigning={handleStartSigningPress}
          onStartSigningFallback={handleStartFallbackSigning}
        />
      )}

      {!signingIframeCrashed ? (
        <div
          id={DOCUSIGN_CONTAINER_ID}
          css={[
            tw`fixed top-0 left-0 h-full w-full bg-[#FFFFFF] overflow-auto z-20`,
            docusignSigningData ? undefined : tw`opacity-0 hidden`,
          ]}
        />
      ) : null}

      <SignContractAgreementModal
        isOpen={isAgreementModalOpen}
        content={agreementContent}
        onClose={handleCloseAgreementModal}
        onSubmit={handleSubmitAgreement}
      />
    </div>
  )
}

export const AcceptCustomerInviteSignContract = memo(AcceptCustomerInviteSignContractComponent)
